import { Autocomplete, TextField } from "@mui/material";

import { SITE_EMPTY_NAME } from "../constants/uiConstants";
import { useAuth } from "../contexts/AuthContext";
import { materialColorsOpacity } from "../design-tokens/material-colors";
import { shape } from "../design-tokens/shape";
import { typography } from "../design-tokens/typography";
import { useSiteNavigation } from "../hooks/useSiteNavigation";

const getSiteDisplayName = (
  name: string | null | undefined,
  id: string,
): string => {
  return name?.trim() || `${SITE_EMPTY_NAME} (${id})`;
};

const darkAutocompleteSx = {
  width: 280,
  "& .MuiInputBase-root": {
    color: "text.primary",
    fontWeight: typography.fontWeight.semiBold,
    fontSize: typography.fontSize["0.8125rem"],
    letterSpacing: "normal",
    textTransform: "uppercase",
    // TODO: Check with design if outline is needed.
    //
    // "& .MuiOutlinedInput-notchedOutline": {
    //   border: "none",
    // },
    // "&:hover .MuiOutlinedInput-notchedOutline": {
    //   border: "none",
    // },
    // "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    //   border: "none",
    // },
  },
  "& .MuiAutocomplete-popupIndicator": {
    color: "text.secondary",
  },
  "& .MuiAutocomplete-clearIndicator": {
    color: "text.secondary",
  },
} as const;

const darkPaperSx = {
  backgroundColor: "background.paper-elevation-reversed",
  borderRadius: `${shape.borderRadius.md}px`,
  border: `1px solid ${materialColorsOpacity.onyx["50-8%"]}`,
  mt: 2,
  "& .MuiAutocomplete-listbox": {
    "& .MuiAutocomplete-option": {
      color: "common.white",
      fontSize: typography.fontSize["1rem"],
      fontWeight: typography.fontWeight.regular,
      padding: "12px 16px",
      "&:hover": {
        backgroundColor: materialColorsOpacity.onyx["50-8%"],
      },
      '&[aria-selected="true"]': {
        backgroundColor: materialColorsOpacity.violet["400-24%"],
      },
      "&.Mui-focused": {
        backgroundColor: materialColorsOpacity.onyx["50-8%"],
      },
    },
  },
  "& .MuiAutocomplete-noOptions": {
    color: materialColorsOpacity.onyx["50-50%"],
  },
} as const;

const darkInputSx = {
  "& .MuiInputBase-input": {
    color: "text.primary",
    fontWeight: typography.fontWeight.semiBold,
    fontSize: typography.fontSize["0.8125rem"],
    letterSpacing: "normal",
    textTransform: "uppercase",
    "&::placeholder": {
      color: materialColorsOpacity.onyx["50-50%"],
      opacity: 1,
    },
  },
} as const;

export function SiteSelectorDark() {
  const { sites } = useAuth();
  const { navigateToSite, currentRouteInfo, siteIdFromParams } =
    useSiteNavigation();

  const availableSites = sites
    .map((site) => {
      return { ...site, name: getSiteDisplayName(site.name, site.id) };
    })
    .sort((a, b) => {
      const aIsEmpty = a.name.startsWith(SITE_EMPTY_NAME);
      const bIsEmpty = b.name.startsWith(SITE_EMPTY_NAME);

      if (aIsEmpty && !bIsEmpty) return -1;
      if (!aIsEmpty && bIsEmpty) return 1;

      return a.name.localeCompare(b.name);
    });

  if (!currentRouteInfo.hasSiteId) return null;

  const selectedSite =
    availableSites.find((s) => s.id === siteIdFromParams) || null;

  return (
    <Autocomplete
      size="small"
      blurOnSelect
      options={availableSites}
      value={selectedSite}
      onChange={(_, newValue) => {
        if (newValue && !newValue.name.startsWith(SITE_EMPTY_NAME)) {
          navigateToSite(newValue.id);
        }
      }}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionDisabled={(option) => option.name.startsWith(SITE_EMPTY_NAME)}
      sx={darkAutocompleteSx}
      slotProps={{
        paper: { sx: darkPaperSx },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          sx={darkInputSx}
          inputProps={{
            ...params.inputProps,
            "aria-label": "Site",
          }}
          id="site-select-dark"
        />
      )}
    />
  );
}
